import { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Renders author-supplied HTML for a dispatch / resource.
 *
 * Wraps the content in a `.dispatch-body` div so authors can scope their CSS
 * (e.g. `.dispatch-body h2 { ... }`) without leaking into the navbar/footer.
 *
 * Sanitizes via DOMPurify on every render — strips <script>, <iframe>, and
 * inline event handlers (onclick, onerror, javascript: URLs, etc.). Inline
 * <style> tags are kept so authors can theme their content.
 */
export type HtmlBodyProps = {
  html: string | null | undefined;
  className?: string;
};

/**
 * Sanitizer configuration. Tight by default — only explicit additions are allowed.
 * Anything not listed gets stripped by DOMPurify's built-in allowlist.
 *
 * What we accept:
 *   - <style> tags (so authors can theme each dispatch)
 *   - common HTML5 content tags (DOMPurify's default allowlist)
 *   - inline `style`, `target`, `rel` attributes
 *
 * What we forbid (defense-in-depth — most are already stripped by default):
 *   - All executable / framing tags
 *   - All `on*` event handlers
 *   - `formaction`, `srcdoc`, `xlink:href`, `xmlns` — known XSS sinks
 */
const FORBID_TAGS = [
  "script",
  "iframe",
  "object",
  "embed",
  "applet",
  "base",
  "form",
  "meta",
  "link",
] as const;

const FORBID_ATTR = [
  // Event handlers — exhaustive list of known on* attributes
  "onabort",
  "onblur",
  "onchange",
  "onclick",
  "oncontextmenu",
  "ondblclick",
  "ondrag",
  "ondragend",
  "ondragenter",
  "ondragleave",
  "ondragover",
  "ondragstart",
  "ondrop",
  "onerror",
  "onfocus",
  "oninput",
  "onkeydown",
  "onkeypress",
  "onkeyup",
  "onload",
  "onmousedown",
  "onmouseenter",
  "onmouseleave",
  "onmousemove",
  "onmouseout",
  "onmouseover",
  "onmouseup",
  "onpointerdown",
  "onpointermove",
  "onpointerup",
  "onreset",
  "onresize",
  "onscroll",
  "onselect",
  "onsubmit",
  "ontoggle",
  "ontouchend",
  "ontouchmove",
  "ontouchstart",
  "onunload",
  "onwheel",
  // Other known XSS sinks
  "formaction",
  "srcdoc",
  "xlink:href",
  "xmlns",
  "ping",
] as const;

const SANITIZER_CONFIG = {
  ADD_TAGS: ["style"],
  ADD_ATTR: ["target", "style", "rel"],
  FORBID_TAGS: [...FORBID_TAGS],
  FORBID_ATTR: [...FORBID_ATTR],
  // Block javascript:, vbscript:, data:text/html: in url-bearing attributes.
  // DOMPurify enforces this by default; ALLOWED_URI_REGEXP makes it explicit.
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel|ftp|sftp|cid):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Strip the contents of any disallowed tag (don't leak the text inside <script>).
  KEEP_CONTENT: false,
  // Don't allow custom elements / unknown tags through.
  ALLOW_UNKNOWN_PROTOCOLS: false,
  // Don't allow data URIs in href (still allowed in <img src> by default).
  ALLOW_DATA_ATTR: false,
};

export default function HtmlBody({ html, className }: HtmlBodyProps) {
  const clean = useMemo(() => {
    if (!html) return "";
    return String(DOMPurify.sanitize(html, SANITIZER_CONFIG));
  }, [html]);

  if (!clean) return null;

  return (
    <div
      className={`dispatch-body${className ? ` ${className}` : ""}`}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
